/**
 * Rendering the school's own identifiers - student number, employee number,
 * course code - now that a school may not issue them at all.
 *
 * Every one of these is nullable (see `services/identifierPolicy.ts` on the API
 * side), and the failure mode is not a crash but a sentence with a hole in it:
 * `{code} - {name}` becomes " - Mathematics", `{name} ({code})` becomes
 * "Mathematics ()", and a detail row labelled "Student ID" sits over blank space
 * that reads as a bug rather than as "this school does not use them".
 *
 * **Screens do not call these directly - they call `useIdentifierText()`.** These
 * helpers answer "is there a value here", which is only half the question: a
 * school that turned student numbers off still has numbers stored against the
 * children it enrolled before it did, and rendering those puts a field on screen
 * the settings page says the school does not have. The hook binds the school's
 * policy to these functions so that half is answered too. What is left here is
 * the pure formatting, and the rules below are its whole job.
 *
 * Two rules, applied everywhere rather than argued about per screen:
 *
 *   - where the identifier is *decoration* on a name, drop it and its punctuation
 *     together. "Mathematics" alone is a complete label; "Mathematics ()" is not.
 *   - where the identifier is the *subject* of the line - a table cell under a
 *     "Code" header, a labelled detail row - show an em dash. The row has to keep
 *     its place in the grid, and a dash is how the rest of this app says "nothing
 *     here" rather than leaving a cell that looks unrendered.
 *
 * Blank strings are treated as absent throughout, matching the API's
 * normalisation: a form that submits '' stores null, so the two must not render
 * differently in the meantime.
 */

/** An identifier that is actually there, or null. Blank counts as absent. */
function present(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * The em dash used for "this school does not record one". Exported so a caller
 * that has to inline it into a larger string matches the cells around it.
 */
export const NO_IDENTIFIER = "—";

/**
 * An identifier as the subject of its own cell or labelled row: the value, or an
 * em dash holding the space.
 */
export function identifierOrDash(value: string | null | undefined): string {
  return present(value) ?? NO_IDENTIFIER;
}

/**
 * A course identified by whichever of its code and name are present.
 *
 * `courseName` is optional because several joined API shapes declare it that way
 * - a payload that omits the join has no name either, and the honest answer there
 * is the code on its own rather than "undefined".
 */
type CourseIdentity = { courseCode?: string | null; courseName?: string | null };

/**
 * A course as "CODE - Name", falling back to whichever half exists. Use where the
 * code leads: a picker option, a list row's title.
 */
export function courseCodeAndName(course: CourseIdentity): string {
  const code = present(course.courseCode);
  const name = present(course.courseName);
  if (code && name) return `${code} - ${name}`;
  return code ?? name ?? "";
}

/**
 * A course as "Name (CODE)", falling back to whichever half exists. Use where the
 * name leads and the code is a parenthetical.
 */
export function courseNameWithCode(course: CourseIdentity): string {
  const code = present(course.courseCode);
  const name = present(course.courseName);
  if (code && name) return `${name} (${code})`;
  return name ?? code ?? "";
}

/**
 * A person as "Name (ID)", falling back to the name alone - the same shape as
 * {@link courseNameWithCode}, for a student number or employee number.
 */
export function nameWithIdentifier(name: string, identifier: string | null | undefined): string {
  const value = present(identifier);
  return value ? `${name} (${value})` : name;
}

/**
 * A prefixed identifier such as "ID: S-1234", or null when there is nothing to
 * prefix. Returning null rather than a bare "ID:" lets the caller drop the whole
 * element, which is the point: an empty label is worse than no label.
 */
export function labelledIdentifier(
  label: string,
  value: string | null | undefined
): string | null {
  const identifier = present(value);
  return identifier ? `${label}: ${identifier}` : null;
}
